import { Container, Graphics } from "pixi.js";
import type { MapSchema } from "@colyseus/schema";

import type { Ship } from "../../../shared/ArenaState";
import { ARENA_SIZE } from "../../../shared/Sailing";

/** Side length of the map, in screen pixels. Square, because the arena is. */
const SIZE = 168;
/** Gap from the screen edge. Matches the leaderboard. */
const MARGIN = 16;
const CORNER_RADIUS = 8;

const PANEL_COLOR = 0x071a21;
const PANEL_ALPHA = 0.55;
const BORDER_COLOR = 0xf2e2c4;
const BORDER_ALPHA = 0.35;
const BORDER_WIDTH = 2;

/** Everyone else. Cream, the same as the nameplates. */
const OTHER_COLOR = 0xf2e2c4;
const OTHER_RADIUS = 3;
/** You. The one warm colour on screen, per design/UI.md's palette. */
const PLAYER_COLOR = 0xc8452f;
const PLAYER_RADIUS = 4.5;
/**
 * An abandoned hull, still sailing and still worth plunder — see design/Battle.md. The same dot
 * as anyone else's, dimmed rather than recoloured: at three pixels a second hue is a guess, and
 * what the map has to say is only "there is a ship there, and it is not fighting back".
 */
const GHOST_ALPHA = 0.4;
/** Length of the line showing which way the player is pointed, in map pixels. */
const HEADING_TICK = 9;

/**
 * The map in the bottom-right corner: every ship in the arena as a dot, the player's in red.
 *
 * It is **north-up and absolute**, not centred on the player — the arena loops, so a
 * player-centred map would have no fixed landmarks to read against, whereas an absolute
 * one lets "I am top-left, they are bottom-right" mean something between glances. The
 * consequence is that the shortest route between two dots may run off one edge and back
 * in the other, which is exactly what the sea does too.
 *
 * Pixi rather than DOM because it shows positions in the sea — see design/UI.md.
 */
export class Minimap {
    private readonly _container = new Container();
    private readonly _dots = new Graphics();

    public get container(): Container {
        return this._container;
    }

    constructor() {
        // The panel never changes, so it is drawn once and left alone.
        const panel = new Graphics().roundRect(0, 0, SIZE, SIZE, CORNER_RADIUS);
        panel.fill({ color: PANEL_COLOR, alpha: PANEL_ALPHA });
        panel.stroke({ color: BORDER_COLOR, alpha: BORDER_ALPHA, width: BORDER_WIDTH });

        this._container.addChild(panel, this._dots);
    }

    /**
     * Redraws every dot. Cheap enough to do from scratch each frame at arena sizes where
     * full-state sync is cheap too, and it needs no bookkeeping to handle ships leaving.
     */
    public update(ships: MapSchema<Ship> | null, localSessionId: string): void {
        this._dots.clear();
        if (!ships) return;

        const scale = SIZE / ARENA_SIZE;

        ships.forEach((ship, sessionId) => {
            const x = ship.x * scale;
            const y = ship.y * scale;

            if (sessionId === localSessionId) {
                // Drawn with the heading tick, and last would be better — but the player
                // dot is larger and coloured, so it reads even when a hull is overlapping.
                this._dots.moveTo(x, y).lineTo(x + Math.sin(ship.heading) * HEADING_TICK, y - Math.cos(ship.heading) * HEADING_TICK);
                this._dots.stroke({ color: PLAYER_COLOR, width: 2 });
                this._dots.circle(x, y, PLAYER_RADIUS).fill(PLAYER_COLOR);
            } else {
                this._dots.circle(x, y, OTHER_RADIUS).fill({ color: OTHER_COLOR, alpha: ship.ghost ? GHOST_ALPHA : 1 });
            }
        });
    }

    public resize(width: number, height: number): void {
        this._container.position.set(width - SIZE - MARGIN, height - SIZE - MARGIN);
    }

    public destroy(): void {
        this._container.destroy({ children: true });
    }
}
